import Card from '@/components/Card';
import { Datatable } from '@/components/Datatable/Datatable';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { TableViewProps } from '@/type/listing.types';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { Select } from '@/components/Controls/Select';
import { getOptionsStatusFilterV2 } from '@/utils/constant.utils';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { useRepairCostingFilter } from '@/hooks/filters/repair-costing-filter.hooks';
import { useGetEquipmentsSelectOptions } from '@/hooks/equipment.hooks';
import { useGetRepairServicesSelectOptions } from '@/hooks/repair-service.hooks';
import { useGetUniqueDamageNamesSelectOptions } from '@/hooks/damage.hooks';
import ButtonAdvancedFilter from '@/components/Custom/Button/ButtonAdvancedFilter';

const limit = 20;

export const RepairCostingTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /**
   * === Filter Section ===
   */
  const {
    filtersEmpty,
    clearFilters,
    equipmentId,
    damageName,
    damageSeverity,
    repairServiceId,
    status,
    searchByEquipmentId,
    searchByDamageName,
    searchByDamageSeverity,
    searchByRepairServiceId,
    searchByStatus,
  } = useRepairCostingFilter({
    setCurrentPage,
  });
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    {
      equipmentId,
      damageName,
      damageSeverity,
      repairServiceId,
      status,
    },
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  const { getAllEquipmentsSelectOptions, isLoadingAllEquipments } =
    useGetEquipmentsSelectOptions();

  const { getAllDamageNamesSelectOptions, isLoadingAllDamages } =
    useGetUniqueDamageNamesSelectOptions();

  const { getAllRepairServicesSelectOptions, isLoadingAllRepairServices } =
    useGetRepairServicesSelectOptions();

  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap justify-between gap-3.5">
          <div className="flex gap-3.5 max-1.5xl:w-full max-1.5xl:justify-between">
            <TableTitle textClassName="mt-1.5" text={t('common:costings')} />
            <div className="hidden max-md:block">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3.5 ml-auto max-1.5xl:ml-0 max-1.5xl:w-full">
            <Select
              containerClassName="min-w-40 max-1.5xl:min-w-0 max-sm:basis-full"
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allEquipments')}
              options={getAllEquipmentsSelectOptions() || []}
              value={equipmentId}
              onChange={(e) => searchByEquipmentId(e.target.value)}
            />
            <Select
              containerClassName="min-w-40 max-1.5xl:min-w-0 max-sm:basis-full"
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allDamages')}
              options={getAllDamageNamesSelectOptions() || []}
              value={damageName}
              onChange={(e) => searchByDamageName(e.target.value)}
            />
            <Select
              containerClassName="min-w-40 max-1.5xl:min-w-0 max-sm:basis-full"
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allSeverities')}
              options={[
                {
                  label: '1',
                  value: 1,
                },
                {
                  label: '2',
                  value: 2,
                },
                {
                  label: '3',
                  value: 3,
                },
              ]}
              value={damageSeverity}
              onChange={(e) => searchByDamageSeverity(e.target.value)}
            />
            <Select
              containerClassName="min-w-40 max-1.5xl:min-w-0 max-sm:basis-full"
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allServices')}
              options={getAllRepairServicesSelectOptions() || []}
              value={repairServiceId}
              onChange={(e) => searchByRepairServiceId(e.target.value)}
            />
            <div className="flex justify-end max-sm:w-full">
              <ButtonAdvancedFilter>
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500 max-w-40"
                  options={getOptionsStatusFilterV2(t)}
                  value={status}
                  onChange={(e) => searchByStatus(e.target.value)}
                />
              </ButtonAdvancedFilter>
            </div>
            <div className="hidden max-1.5xl:block max-md:hidden">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
            <div className="max-1.5xl:hidden">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
        </div>
        <div>
          <Datatable
            t={t}
            headers={<TableHeaders t={t} onSort={handleSort} />}
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </Card>
      {(isLoadingAllData ||
        isPendingAllData ||
        pendingDeactivate ||
        isLoadingAllEquipments ||
        isLoadingAllDamages ||
        isLoadingAllRepairServices) && <Loader />}
    </>
  );
};
